<script lang="ts">
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import type { ProductInquiryInput } from "../../../core/schema/ProductInquiry.js";
	import sendProductInquiry from "../api/mutations/sendProductInquiry.graphql";
	import { mutation } from "../api/graphql.js";
	import Modal from "./Modal.svelte";
	import Form from "./Form/Form.svelte";
	import LabelledInput from "./Form/LabelledInput.svelte";
	import GdprCheckbox from "./Form/GdprCheckbox.svelte";
	import EmailMarketingCheckbox from "./Form/EmailMarketingCheckbox.svelte";
	import RecaptchaDisclaimer from "./Form/RecaptchaDisclaimer.svelte";
	import Button from "./Button.svelte";
	import Message from "./Message.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import translations from "./ProductInquiryModal.translations.json";
	import { getTranslate } from "../utils/getTranslate.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getTenant } from "../getTenant.js";

	export let show = false;
	export let productId = "";
	export let productName = "";

	const translate = getTranslate(translations);
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const tenant = getTenant();

	const send = createPromiseStore(async (input: ProductInquiryInput) => {
		await mutation<boolean>(PUBLIC_GRAPHQL_ENDPOINT, sendProductInquiry, { input });
	});

	let submitted = false;

	function reset(): void {
		submitted = false;
	}

	$: if (!show) {
		reset();
	}

	async function submit(event: CustomEvent<SubmitEvent>): Promise<void> {
		const form = event.detail.target as HTMLFormElement;
		const formData = new FormData(form);
		const emailMarketingApprovedAt = formData.get("emailMarketingApprovedAt") as string;
		const input: ProductInquiryInput = {
			email: (formData.get("email") as string).trim(),
			gdprApprovedAt: formData.get("gdprApprovedAt") as string,
			message: (formData.get("message") as string).trim(),
			name: (formData.get("name") as string).trim(),
			pageUrl: window.location.href,
			phoneNumber: (formData.get("phoneNumber") as string).trim(),
			productId,
			productName,
			locale: tenant.locale,
			...(emailMarketingApprovedAt && { emailMarketingApprovedAt }),
		};
		await send.call(input);
		if ($send.status === "success") {
			form.reset();
			submitted = true;
		}
	}
</script>

<Modal
	bind:show
	class="border-lttertiary !bg-ltwhiteout !w-[50rem] !max-w-[calc(100vw-1rem)] !border !p-0"
	headerClass="p-8 pb-0"
	closeLabel={translate("close")}
	noBackdrop
>
	<h2 slot="title" class="text-ltprimary m-0 font-light uppercase tracking-wider md:text-2xl">
		{translate("title")}
	</h2>

	{#if submitted}
		<div class="flex flex-col items-start gap-4 p-8">
			<p class="text-ltprimary font-bahnschrift text-xl font-medium uppercase tracking-wider">
				{translate("thanks")}
			</p>
			<p class="text-ltsecondary text-base/[1.6] tracking-[0.02em]">
				{translate("willBeInTouch")}
			</p>
			<Button
				icon={arrowRight}
				on:click={() => {
					show = false;
				}}
			>
				{translate("understand")}
			</Button>
		</div>
	{:else}
		<p class="text-ltsecondary px-8 pb-8 text-base/[1.6] tracking-[0.02em]">
			{translate("desc")}
		</p>

		<Form on:submit={submit}>
			<div class="grid w-full items-end">
				<LabelledInput
					class="border-lttertiary border-y [&_.input]:!px-8 [&_.label]:!left-8"
					id="name"
					required
					autocomplete="given-name"
				>
					{translate("name")}
				</LabelledInput>
				<LabelledInput
					class="border-lttertiary border-b [&_.input]:!px-8 [&_.label]:!left-8"
					id="phoneNumber"
					required
					type="tel"
					autocomplete="tel"
					inputProps={{ pattern: "[+]?[\\d ]+" }}
				>
					{translate("phone")}
				</LabelledInput>
				<LabelledInput
					class="border-lttertiary border-b [&_.input]:!px-8 [&_.label]:!left-8"
					id="email"
					required
					type="email"
					autocomplete="email"
				>
					{translate("email")}
				</LabelledInput>
			</div>

			<div class="relative w-full">
				<textarea
					id="message"
					name="message"
					class="input-with-label border-lttertiary peer h-48 w-full border-b px-8 !pb-4 !pt-8 text-xs focus:font-semibold focus:outline-none"
					required
					minlength="10"
				></textarea>
				<label class="label textarea-label !left-8" for="message">
					{translate("yourMessage")}
				</label>
			</div>

			<div class="flex flex-col justify-between gap-5 p-6 md:p-8 lg:flex-row lg:items-center">
				<div class="flex min-w-0 flex-col gap-2 lg:flex-1">
					<GdprCheckbox class="justify-start" />
					<EmailMarketingCheckbox class="justify-start" />
				</div>
				<div class="shrink-0">
					<Button
						class="mx-[initial]"
						icon={arrowRight}
						type="submit"
						variant="primary"
						disabled={$send.status === "loading"}
					>
						{#if $send.status === "loading"}
							{translate("submitting")}…
						{:else}
							{translate("submit")}
						{/if}
					</Button>
				</div>
			</div>

			{#if $send.status === "error"}
				<Message variant="error" class="mx-8 mb-8">{translate("somethingWrong")}</Message>
			{/if}

			<RecaptchaDisclaimer class="mx-8 mb-8" />
		</Form>
	{/if}
</Modal>
